// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import BulkActions from '../slideout/BulkActions';
import SlidingPane from 'react-sliding-pane';
import UploadKeys from '../slideout/UploadKeys';
import useUploadNotificationsSingle from '../../../hooks/useUploadNotificationsSingle';
import { toast } from 'react-toastify';
import SteamBulkRequest from '../slideout/SteamBulkRequest';
import ReassignBulk from '../slideout/ReassignBulk';
import ChangeStatusBulk from '../slideout/ChangeStatusBulk';
import ReturnKeys from '../slideout/ReturnKeys';
import RequestsTableForUserTypeAndDisplayMode from './RequestLists/RequestsTableForUserTypeAndDisplayMode';
import { useTranslation } from 'react-i18next';
import { downloadKeys } from '../../../api/requestApi';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { addToGroup } from '../../../middleware/signalRMiddleware';

export interface RequestsTableProps {
  scope?: Record<string, unknown>;
  assignedToMe?: boolean;
  settingsKey?: string;
  /** localStorage key for grouped view preference; defaults to 'requestsGrouped' */
  groupedStorageKey?: string;
  title?: string;
  reload?: unknown;
  setReload?: (r: unknown) => void;
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
  setBreadcrumb?: (b: unknown) => void;
}

function RequestsTable({
  scope,
  assignedToMe,
  settingsKey,
  groupedStorageKey = 'requestsGrouped',
  title,
  reload,
  setReload,
  setCompleteRequestSlideoutOpen,
  setBreadcrumb,
}: RequestsTableProps) {
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth);
  const importTransactions = useAppSelector((state) => state.importTransactions);
  const dispatch = useAppDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [itemsChecked, setItemsChecked] = useState([]);
  const [bulkActionsSlideoutOpen, setBulkActionsSlideoutOpen] = useState(false);
  const [uploadKeysSlideoutItem, setUploadKeysSlideoutItem] = useState();
  const [uploadKeysSlideoutOpen, setUploadKeysSlideoutOpen] = useState(false);
  const [requestsGrouped, setRequestsGrouped] = useLocalStorage(
    groupedStorageKey,
    false
  );
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const groupedParam = searchParams.get('grouped');
    if (groupedParam === 'true') {
      setRequestsGrouped(true);
    } else if (groupedParam === 'false') {
      setRequestsGrouped(false);
    }
  }, [searchParams, setRequestsGrouped]);

  useEffect(() => {
    if (searchParams.get('grouped') === null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('grouped', String(requestsGrouped));
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, requestsGrouped, setSearchParams]);

  const handleSetRequestsGrouped = (value: boolean) => {
    setRequestsGrouped(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('grouped', String(value));
        return next;
      },
      { replace: true }
    );
  };

  const found = (foundTransactions) => {
    if (foundTransactions.length === 1) {
      const transaction = foundTransactions.slice(-1)[0];
      if (transaction.success === true) {
        toast.success(transaction.message);
        setUploadsInProgress((prevState) =>
          prevState.filter((id) => id !== transaction.id)
        );
        setReload(new Date());
      } else if (transaction.success === false) {
        toast.error(transaction.message);
        setUploadsInProgress((prevState) =>
          prevState.filter((id) => id !== transaction.id)
        );
        setReload(new Date());
      }
    }
  };
  const [uploadsInProgress, setUploadsInProgress] =
    useUploadNotificationsSingle('import', importTransactions, found);

  const handleDownloadKeys = (e, request) => {
    e.cancelBubble = true;
    setIsProcessing(true);
    downloadKeys(request.id)
      .then(() => {
        setIsProcessing(false);
      })
      .catch((e) => {
        toast.error(e.message || t('Failed to download keys'));
        setIsProcessing(false);
      });
  };

  const handleUploadKeys = (e, item) => {
    if (e) {
      e.cancelBubble = true;
    }
    setUploadKeysSlideoutItem(item);
    setUploadKeysSlideoutOpen(true);
    return false;
  };

  const [bulkStatusChangeSlideoutOpen, setBulkStatusChangeSlideoutOpen] =
    useState(false);
  const [
    bulkStatusChangeSlideoutRequests,
    setBulkStatusChangeSlideoutRequests,
  ] = useState();

  const [bulkReassignSlideoutOpen, setBulkReassignSlideoutOpen] =
    useState(false);
  const [bulkReassignSlideoutRequests, setBulkReassignSlideoutRequests] =
    useState();

  const [bulkSteamRequestSlideoutOpen, setBulkSteamRequestSlideoutOpen] =
    useState(false);
  const [bulkSteamRequestSlideoutSKUs, setBulkSteamRequestSlideoutSKUs] =
    useState();

  const handleBulkStatusChange = (e, row) => {
    setBulkStatusChangeSlideoutRequests([row.requestId ?? row.id]);
    setBulkStatusChangeSlideoutOpen(true);
  };

  const handleBulkReassign = (e, row) => {
    setBulkReassignSlideoutRequests([row.id]);
    setBulkReassignSlideoutOpen(true);
  };

  const handleBulkSteamRequest = (e, row) => {
    let skus = [];
    if (row.games) {
      for (const j in row.games) {
        const requestRow = row.games[j];
        if (requestRow.canOrderKeys) {
          skus = addSteamRequestItem(requestRow, requestRow.requestId, skus);
        }
      }
    } else if (row.canOrderKeys) {
      skus = addSteamRequestItem(row, row.id, skus);
    }
    if (skus.length > 0) {
      setBulkSteamRequestSlideoutSKUs(skus);
      setBulkSteamRequestSlideoutOpen(true);
    } else {
      toast.error(
        t('None of the selected Key Requests are in the correct state')
      );
    }
  };

  const [returnKeysSlideoutOpen, setReturnKeysSlideoutOpen] = useState(false);
  const [returnKeysSlideoutItem, setReturnKeysSlideoutItem] = useState();
  const handleReturnKeys = (e, item) => {
    e.cancelBubble = true;
    setReturnKeysSlideoutOpen(true);
    setReturnKeysSlideoutItem(item);
    return false;
  };

  const addSteamRequestItem = (row, keyRequestId, skus) => {
    const keyPoolRow = {
      keyWorkflowId: row.keyWorkflowId,
      keyWorkflowName: row.keyWorkflowName,
      keyProviderReference: row.keyProviderReference,
      requesterTag: row.requesterTag,
      numberOfKeys: row.numberOfKeys,
    };
    skus.push({
      skuId: row.skuId,
      gameName: row.gameTitle,
      skuName: row.skuName,
      keyRequestId: keyRequestId,
      packageTypeId: row.packageTypeId,
      keyPools: [keyPoolRow],
    });
    return skus;
  };

  return (
    <>
      <RequestsTableForUserTypeAndDisplayMode
        assignedToMe={assignedToMe}
        title={title}
        reload={reload}
        itemsChecked={itemsChecked}
        setItemsChecked={setItemsChecked}
        requestsGrouped={requestsGrouped}
        setRequestsGrouped={handleSetRequestsGrouped}
        currentUser={currentUser}
        setBulkActionsSlideoutOpen={setBulkActionsSlideoutOpen}
        setCompleteRequestSlideoutOpen={setCompleteRequestSlideoutOpen}
        setBreadcrumb={setBreadcrumb}
        scope={scope}
        settingsKey={settingsKey}
        handleDownloadKeys={handleDownloadKeys}
        handleUploadKeys={handleUploadKeys}
        handleBulkStatusChange={handleBulkStatusChange}
        handleBulkReassign={handleBulkReassign}
        handleBulkSteamRequest={handleBulkSteamRequest}
        handleReturnKeys={handleReturnKeys}
        isProcessing={isProcessing}
      />
      <SlidingPane
        isOpen={bulkActionsSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setBulkActionsSlideoutOpen(false)}
      >
        <BulkActions
          done={() => {
            setReload(new Date());
            setItemsChecked([]);
            setBulkActionsSlideoutOpen(false);
          }}
          cancel={() => setBulkActionsSlideoutOpen(false)}
          items={itemsChecked}
        ></BulkActions>
      </SlidingPane>

      <SlidingPane
        isOpen={uploadKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setUploadKeysSlideoutOpen(false)}
      >
        <>
          {uploadKeysSlideoutItem && (
            <UploadKeys
              done={(d) => {
                if (!uploadsInProgress.includes(d.id)) {
                  dispatch(addToGroup(`import:${d.id}`));
                  setUploadsInProgress((prevState) =>
                    prevState.includes(d.id) ? prevState : [...prevState, d.id]
                  );
                }
                setReload(new Date());
                setUploadKeysSlideoutOpen(false);
              }}
              cancel={() => setUploadKeysSlideoutOpen(false)}
              requestId={uploadKeysSlideoutItem?.id}
              gameName={uploadKeysSlideoutItem?.gameTitle}
              skuName={uploadKeysSlideoutItem?.skuName}
              keyProviderName={uploadKeysSlideoutItem?.keyProviderName}
              packageId={uploadKeysSlideoutItem?.packageId}
              numberOfKeys={uploadKeysSlideoutItem?.numberOfKeys}
              requesterTag={uploadKeysSlideoutItem?.requesterTag}
              file={uploadKeysSlideoutItem?.file}
            />
          )}
        </>
      </SlidingPane>

      <SlidingPane
        isOpen={bulkStatusChangeSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setBulkStatusChangeSlideoutOpen(false)}
      >
        <ChangeStatusBulk
          done={() => {
            setReload(new Date());
            setBulkStatusChangeSlideoutOpen(false);
          }}
          cancel={() => setBulkStatusChangeSlideoutOpen(false)}
          requests={bulkStatusChangeSlideoutRequests}
        ></ChangeStatusBulk>
      </SlidingPane>

      <SlidingPane
        isOpen={bulkReassignSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setBulkReassignSlideoutOpen(false)}
      >
        <ReassignBulk
          done={() => {
            setReload(new Date());
            setBulkReassignSlideoutOpen(false);
          }}
          cancel={() => setBulkReassignSlideoutOpen(false)}
          requests={bulkReassignSlideoutRequests}
        ></ReassignBulk>
      </SlidingPane>

      <SlidingPane
        isOpen={bulkSteamRequestSlideoutOpen}
        hideHeader={true}
        from="right"
        className="large-side-panel"
        onRequestClose={() => setBulkSteamRequestSlideoutOpen(false)}
      >
        <SteamBulkRequest
          done={() => {
            setReload(new Date());
            setBulkSteamRequestSlideoutOpen(false);
          }}
          cancel={() => setBulkSteamRequestSlideoutOpen(false)}
          skus={bulkSteamRequestSlideoutSKUs}
        ></SteamBulkRequest>
      </SlidingPane>

      <SlidingPane
        isOpen={returnKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setReturnKeysSlideoutOpen(false)}
      >
        <ReturnKeys
          request={returnKeysSlideoutItem}
          done={() => {
            setReturnKeysSlideoutOpen(false);
            setReload(new Date());
          }}
          cancel={() => setReturnKeysSlideoutOpen(false)}
        ></ReturnKeys>
      </SlidingPane>
    </>
  );
}

export default RequestsTable;
